<template>
  <el-dialog
      v-loading="loading"
      :fullscreen="true"
      :close-on-press-escape="false"
      :show-close="false"
      :close-on-click-modal="false"
      class="refund-request-dialog"
      :visible.sync="visible">
    <div
        id="refund-request-title"
        class="refund-request-title-bar">
      <button
          v-if="!showFinalStep"
          type="button"
          class="el-dialog__headerbtn"
          style="right: unset; left: 20px"
          aria-label="Back"
          @click="back">
        <i class="el-dialog__close el-icon el-icon-arrow-left"></i>
      </button>
      <div class="order-number">
        Refund for order #{{
          order.order.displayable_order_id || order.order.order_id
        }}
      </div>
      <button
          type="button"
          class="el-dialog__headerbtn"
          aria-label="Close"
          @click="$emit('update:visible', false)">
        <i class="el-dialog__close el-icon el-icon-close"></i>
      </button>
      <StepProgressBar
          :total-steps="totalSteps"
          :current-step="currentStep" />
    </div>
    <div
        v-if="!loading"
        id="refund-request-body"
        class="refund-request-body">
      <div class="refund-request-steps">
        <el-alert
            v-if="showInstallationWarning"
            title="Installation Service — Do Not Refund"
            type="warning"
            :closable="false"
            show-icon
            style="margin-bottom: 16px">
          <span>
            Do not process refunds on the Installation ASIN (IG-5X-601-PIN).
            Check your resources for guidance or reach out to leadership.
          </span>
        </el-alert>
        <StepFlowButton
            buttontext="Step 1: Select Items"
            :isactive="isactive1"
            :current-value="1"
            :disabled="isdisabled1"
            @go-back="previous" />
        <StepFlowButton
            buttontext="Step 2: Review & Confirm"
            :isactive="isactive2"
            :current-value="2"
            :disabled="isdisabled2"
            @go-back="previous" />
      </div>
      <div style="border: 1px solid #DEE5EC;"></div>
      <div
          id="refund-request-data"
          class="refund-request-data">
        <RefundSelectItems
            v-if="currentStep === 1"
            :loading.sync="loading"
            :refundable-items="refundableItems"
            :shipping-total="shippingTotal"
            :already-selected="allItemsWithAlreadySelected"
            :refund-reasons="refundReasons"
            :refund-shipping.sync="refundShipping"
            @get-selected-items-for-refund="setSelectedItemsForRefund" />
        <ReviewRefundSummary
            v-if="currentStep === 2 && !showFinalStep"
            :loading.sync="loading"
            :customer="customerDetails"
            :items-to-refund="selectedItems"
            :shipping-total="shippingTotal"
            :refund-shipping="refundShipping" />
        <RefundSummary
            v-if="currentStep === 2 && showFinalStep"
            :loading.sync="loading"
            :customer="customerDetails"
            :items-to-refund="selectedItems"
            :shipping-total="shippingTotal"
            :refund-shipping="refundShipping" />
      </div>
    </div>
    <div
        id="refund-request-footer"
        class="refund-request-footer">
      <el-checkbox
          v-if="showRefundSummaryConfirmationLink"
          v-model="isSummaryConfirmed"
          class="confirm-checkbox"
          style="margin-right: 24px">
        The items for refund are correct.
      </el-checkbox>
      <el-button
          v-if="showBack"
          class="action_button back"
          @click="back">
        Back
      </el-button>
      <el-button
          v-if="showNext"
          type="primary"
          :disabled="isNextDisabled"
          class="action_button"
          @click="next">
        Next
      </el-button>
      <el-button
          v-if="currentStep == 2 && !showFinalStep"
          type="primary"
          :disabled="isProcessRefundDisabled"
          class="action_button"
          @click="processRefund">
        Process Refund
      </el-button>
      <el-button
          v-if="currentStep == 2 && showFinalStep"
          type="primary"
          class="action_button"
          @click="exit">
        Done
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import StepProgressBar from "../StepProgressBar.vue";
import StepFlowButton from "../Return/StepFlowButton.vue";
import RefundSelectItems from "./RefundSelectItems.vue";
import RefundSummary from "./RefundSummary.vue";
import axios from "axios";
import ReviewRefundSummary from "./ReviewRefundSummary.vue";

import { createNamespacedHelpers } from "vuex";
const { mapGetters: sharedGetters } = createNamespacedHelpers("shared");

export default {
  name: "RefundDialog",
  components: {
    StepProgressBar,
    StepFlowButton,
    RefundSelectItems,
    RefundSummary,
    ReviewRefundSummary,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    order: {
      type: Object,
      required: true,
    },
    totalTax: {
      type: Object,
      required: true,
      default() {
        return {
          amount: 0,
          currency_code: "USD",
        };
      },
    },
    isLisbonOrder: {
      type: Boolean,
      default: false,
    },
    showInstallationWarning: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      currentStep: 1,
      totalSteps: 2,
      areItemSelectionsComplete: false,
      isactive1: true,
      isactive2: false,
      isdisabled1: false,
      isdisabled2: false,
      isSummaryConfirmed: false,
      displayFinalStep: false,
      customerDetails: {},
      refundableItems: [],
      refundReasons: [],
      refundPaymentInfo: {},
      refundInfo: {},
      refundShipping: false,
      shippingTotal: 0,
      selectedItems: [],
      refundLineItems: [],
      allItemsWithAlreadySelected: [],
    };
  },
  computed: {
    ...sharedGetters(["reasons"]),
    showBack() {
      return this.currentStep > 1 && !this.displayFinalStep;
    },
    showNext() {
      return this.currentStep < 2;
    },
    isNextDisabled() {
      if (this.currentStep === 1) {
        return (
          !this.areItemSelectionsComplete &&
          (this.selectedItems.length > 0 || !this.refundShipping)
        );
      }
    },
    showRefundSummaryConfirmationLink() {
      return this.currentStep === 2 && !this.displayFinalStep;
    },
    isProcessRefundDisabled() {
      return this.currentStep === 2 && !this.isSummaryConfirmed;
    },
    showFinalStep() {
      return this.currentStep === 2 && this.displayFinalStep;
    },
  },
  watch: {
    currentStep() {
      if (this.currentStep === 1) {
        this.isactive1 = true;
        this.isactive2 = false;
      }
      if (this.currentStep === 2) {
        this.isactive1 = false;
        this.isactive2 = true;
      }
    },
  },
  mounted() {
    this.loading = false;
    this.customerDetails = this.order.customer;
    if (this.currentStep === 1) {
      this.areItemsSelected = false;
      this.loadRefundableItems();
      this.loadRefundReasons();
    }
  },
  methods: {
    previous(curValue) {
      if (curValue < this.currentStep && this.currentStep > 1) {
        this.currentStep = this.currentStep - (this.currentStep - curValue);
      }
    },
    back() {
      if (this.currentStep === 1) this.$emit("update:visible", false);
      else this.currentStep--;
    },
    next() {
      if (this.currentStep === this.totalSteps) return;
      this.currentStep = this.currentStep + 1;
    },
    exit() {
      this.$emit("update:visible", false);
      this.$emit("exit-order-details", true);
    },
    processRefund() {
      this.loading = true;

      return axios
        .post("v3/refunds/partial_refund", {
          order_id: this.order.order.displayable_order_id,
          refund_shipping: this.refundShipping,
          refund_line_items: this.refundLineItems,
        })
        .then((response) => {
          this.$message({
            type: "success",
            message: `Refund for order #${this.order.order.displayable_order_id} has been processed successfully`,
          });

          this.isdisabled1 = true;
          this.isdisabled2 = true;
          this.displayFinalStep = true;
        })
        .catch((error) => {
          console.log(error);
          this.$message({
            type: "error",
            message: `Refund for the order #${this.order.order.displayable_order_id} has not been processed. Try again.`,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadRefundableItems() {
      this.refundableItems = this.order.order_items;

      const stripe_order_details = this.order.stripe_order_details;
      this.shippingTotal = parseFloat(
        (
          stripe_order_details.shippingTotal -
          stripe_order_details.refundShipping -
          stripe_order_details.refundShippingTax
        ).toFixed(2)
      );
    },
    loadRefundReasons() {
      axios
        .get("/v2/reasons.json?active=true")
        .then((result) => {
          const refundGroupIds = result.data.groups
            .filter((group) => group.key === "refund")
            .map((group) => group.id);
          this.refundReasons = result.data.reasons.filter((reason) =>
            refundGroupIds.includes(reason.reason_group_id)
          );
        })
        .catch((error) => console.log(error));
    },
    setSelectedItemsForRefund(selections) {
      this.allItemsWithAlreadySelected = selections;
      this.selectedItems = selections.filter((val) => val.selected);

      const validRefundLineItems = this.selectedItems.filter((item) => {
        if (item.reason == null || !item.isValidRefund) return false;
        if (item.bundle_items != null) {
          item.bundle_items.forEach((bundle_item) => {
            if (bundle_item.reason_id == null) return false;
          });
        }

        return true;
      });

      this.areItemSelectionsComplete =
        this.selectedItems.length === validRefundLineItems.length &&
        validRefundLineItems.length > 0;

      this.setRefundLineItems(validRefundLineItems);
    },
    setRefundLineItems(validRefundLineItems) {
      const refundLineItems = [];

      for (let i = 0; i < validRefundLineItems.length; i++) {
        const stripeOrder = validRefundLineItems[i].stripe_order_item;

        const refundLineItem = {
          line_item_id: stripeOrder.id,
          amount: this.isLisbonOrder
            ? validRefundLineItems[i].refundAmount
            : validRefundLineItems[i].refundAmount,
          refund_type: validRefundLineItems[i].refundType,
          reason_id: validRefundLineItems[i].reason,
          sku: stripeOrder.asin,
        };
        refundLineItems.push(refundLineItem);
      }

      this.refundLineItems = refundLineItems;
    },
    setRefundShipping(value) {
      this.refundShipping = value;
    },
  },
};
</script>

<style scoped>
.refund-request-title-bar {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  z-index: 9;
  height: 64px;
  background: #ffffff;
}

.refund-request-title-bar .order-number {
  position: absolute;
  height: 24px;
  left: 72px;
  right: 72px;
  top: calc(50% - 24px / 2);
  font-family: "Equip", sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
  display: table-cell;
  align-items: center;
  text-align: center;
  color: #424242;
}

.refund-request-body {
  top: 64px;
  bottom: 64px;
  margin-bottom: 64px;
  position: relative;
  display: flex;
  flex-direction: row;
  overflow-y: scroll;
  min-height: calc(100% - 160px);
  background: rgb(250, 251, 252);
}

.refund-request-body .refund-request-steps {
  width: 532px;
  flex: 1;
  background: #ffffff;
  padding: 40px;
}

.refund-request-body .refund-request-data {
  flex: 3;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  padding: 40px;
}

.refund-request-footer {
  position: relative;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9;
  height: 64px;
  background: #ffffff;
  filter: drop-shadow(0px -1px 4px rgba(0, 0, 0, 0.15));
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: 16px 40px;
}

.action_button {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px 16px !important;
  gap: 8px;
  width: 277px;
  height: 48px;
  flex: none;
  order: 0;
  flex-grow: 0;
  border: 1px solid #1998d5 !important;
  border-radius: 8px !important;
  font-family: "Equip", sans-serif;
  font-style: normal;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 28px !important;
  text-align: center !important;
}

.action_button.back {
  color: #1998d5 !important;
}

.confirm-checkbox {
  font-style: normal;
  font-weight: 300;
  font-size: 14px;
  line-height: 24px;
  color: #424242;
}
</style>
